import React, { useState } from 'react';
import { Bell, ChevronDown, Globe, Check, Menu, X, LogIn } from 'lucide-react';
import { lionLogo, whitepaperUrl } from '../mock';
import { useLang } from '../LangContext';
import { useAuth } from '../AuthContext';
import Dashboard from './Dashboard';
import AuthModal from './AuthModal';

const LANGS = [
  { code: 'en', label: 'EN' },
  { code: 'fr', label: 'FR' },
  { code: 'es', label: 'ES' },
];

const Header = () => {
  const { lang, setLang, t } = useLang();
  const { user } = useAuth();
  const [active, setActive] = useState('PRESALE');
  const [langOpen, setLangOpen] = useState(false);
  const [dashOpen, setDashOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const navLinks = [
    { key: 'PRESALE', label: t.nav.presale, href: '#presale', badge: 'HOT' },
    { key: 'GAMES', label: t.nav.games, href: '#games' },
    { key: 'TOKENOMICS', label: t.nav.tokenomics, href: '#tokenomics' },
    { key: 'STAKING', label: t.nav.staking, href: '#staking' },
    { key: 'ROADMAP', label: t.nav.roadmap, href: '#roadmap', pdf: true },
    { key: 'WHITEPAPER', label: t.nav.whitepaper, href: '#whitepaper', pdf: true },
    { key: 'FAQ', label: t.nav.faq, href: '#faq' },
  ];

  const handleNav = (e, link) => {
    e.preventDefault();
    setActive(link.key);
    setMobileOpen(false);
    if (link.pdf) {
      window.open(whitepaperUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    const id = link.href.replace('#', '');
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const balanceDisplay = user ? `${(user.walletBalance ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} DOMIA` : '';

  return (
    <header className="relative z-40 w-full">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-4 md:py-5 flex items-center justify-between gap-3">
        {/* Logo — no rigid frame, just the lion image floating beside text */}
        <div className="flex items-center gap-2 md:gap-3 shrink-0">
          <img
            src={lionLogo}
            alt="Domino Arena"
            className="w-14 h-14 md:w-16 md:h-16 object-contain drop-shadow-[0_8px_18px_rgba(30,64,175,0.35)]"
          />
          <div className="leading-none">
            <div className="text-[20px] md:text-[24px] font-black text-slate-900 tracking-tight">DOMINO</div>
            <div className="text-[18px] md:text-[22px] font-black -mt-1 bg-gradient-to-r from-blue-700 to-blue-400 bg-clip-text text-transparent tracking-tight">ARENA</div>
            <div className="hidden md:block text-[8px] tracking-[0.3em] text-slate-500 mt-0.5">PLAY • WIN • EARN</div>
          </div>
        </div>

        {/* Nav - Desktop */}
        <nav className="hidden lg:flex items-center gap-1 bg-white/70 backdrop-blur-md rounded-full px-3 py-2 border border-white shadow-[0_8px_20px_rgba(15,23,42,0.05)]">
          {navLinks.map((l) => (
            <a key={l.key} href={l.href} onClick={(e) => handleNav(e, l)} className={`nav-link ${active === l.key ? 'active' : ''}`}>
              {l.label}
              {l.badge && <span className="hot-badge">{l.badge}</span>}
            </a>
          ))}
        </nav>

        {/* Right actions */}
        <div className="flex items-center gap-2 md:gap-3">
          {/* Language */}
          <div className="relative">
            <button onClick={() => setLangOpen(v => !v)} className="flex items-center gap-1.5 bg-white/80 border border-slate-200 rounded-full px-2.5 md:px-3 py-2 hover:bg-white transition">
              <Globe className="w-4 h-4 text-slate-700" />
              <span className="text-[12px] font-extrabold text-slate-700">{lang.toUpperCase()}</span>
              <ChevronDown className="w-3.5 h-3.5 text-slate-500" />
            </button>
            {langOpen && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setLangOpen(false)} />
                <div className="absolute right-0 mt-2 w-32 bg-white rounded-2xl border border-slate-200 shadow-xl py-1.5 z-40">
                  {LANGS.map(l => (
                    <button key={l.code} onClick={() => { setLang(l.code); setLangOpen(false); }} className="w-full text-left px-4 py-2 hover:bg-blue-50 flex items-center justify-between text-[13px] font-bold text-slate-800">
                      {l.label}
                      {lang === l.code && <Check className="w-3.5 h-3.5 text-blue-600" />}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {user ? (
            <>
              <button className="relative bg-white/80 border border-slate-200 rounded-full p-2.5 hover:bg-white transition hidden sm:block">
                <Bell className="w-4 h-4 text-slate-700" />
              </button>
              <button onClick={() => setDashOpen(true)} className="flex items-center gap-2 bg-white/85 border border-slate-200 rounded-full pl-1.5 pr-2 md:pr-4 py-1.5 hover:border-blue-300 transition">
                <div className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-gradient-to-br from-blue-700 to-blue-400 flex items-center justify-center text-white font-black text-[15px]">
                  {user.username?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <div className="leading-tight text-left hidden sm:block">
                  <div className="text-[13px] font-bold text-slate-900">{user.username}</div>
                  <div className="text-[10px] text-amber-600 font-semibold">{balanceDisplay}</div>
                </div>
              </button>
            </>
          ) : (
            <button onClick={() => setAuthOpen(true)} className="flex items-center gap-2 rounded-full px-4 py-2.5 font-extrabold text-white text-[13px] shadow-[0_8px_18px_rgba(37,99,235,0.4)]" style={{ background: 'linear-gradient(90deg, #1d4ed8, #3b82f6)' }}>
              <LogIn className="w-4 h-4" />
              Sign In
            </button>
          )}

          {/* Mobile hamburger */}
          <button onClick={() => setMobileOpen(v => !v)} className="lg:hidden bg-white/80 border border-slate-200 rounded-full p-2.5 hover:bg-white transition">
            {mobileOpen ? <X className="w-4 h-4 text-slate-700" /> : <Menu className="w-4 h-4 text-slate-700" />}
          </button>
        </div>
      </div>

      {/* Mobile menu drawer */}
      {mobileOpen && (
        <>
          <div className="lg:hidden fixed inset-0 bg-black/40 backdrop-blur-sm z-40" onClick={() => setMobileOpen(false)} />
          <nav className="lg:hidden absolute top-full left-3 right-3 mt-2 bg-white rounded-2xl border border-slate-200 shadow-xl p-3 z-50 flex flex-col gap-1">
            {navLinks.map((l) => (
              <a key={l.key} href={l.href} onClick={(e) => handleNav(e, l)} className={`flex items-center justify-between px-4 py-3 rounded-xl font-bold text-[14px] transition ${active === l.key ? 'bg-blue-50 text-blue-700' : 'text-slate-800 hover:bg-slate-50'}`}>
                <span>{l.label}</span>
                {l.badge && <span className="text-[10px] font-extrabold text-white bg-gradient-to-r from-orange-500 to-red-500 rounded-full px-2 py-0.5">{l.badge}</span>}
              </a>
            ))}
          </nav>
        </>
      )}

      <Dashboard open={dashOpen} onClose={() => setDashOpen(false)} />
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />
    </header>
  );
};

export default Header;
